import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, Send, Search, Filter, ChevronRight, User, Clock, 
  AlertCircle, Check, CheckCheck, Sparkles, Bot, Bell, RefreshCw,
  FileText, Smile, X
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { api } from '../utils/api';

const MessagesInbox = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRisk, setFilterRisk] = useState('all');
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedThread) {
      loadThreadMessages(selectedThread.patient_id);
    }
  }, [selectedThread]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [threadsRes, templatesRes] = await Promise.all([
        api.getMessageThreads(),
        api.getMessageTemplates(),
      ]);
      setThreads(threadsRes.data);
      setTemplates(templatesRes.data);
      
      // Auto-select first thread if available
      if (threadsRes.data.length > 0 && !selectedThread) {
        setSelectedThread(threadsRes.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const loadThreadMessages = async (patientId) => {
    try {
      const response = await api.getPatientMessages(patientId);
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThread) return;

    try {
      setSendingMessage(true);
      await api.sendMessage({
        patient_id: selectedThread.patient_id,
        to_user_id: selectedThread.patient_id,
        to_user_name: selectedThread.patient_name,
        message: newMessage,
        message_type: 'manual',
      });
      
      setNewMessage('');
      await loadThreadMessages(selectedThread.patient_id);
      await loadData(); // Refresh thread list
      toast.success('Message sent!');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleUseTemplate = (template) => {
    let msg = template.body;
    if (selectedThread) {
      msg = msg.replace('{patient_name}', selectedThread.patient_name.split(' ')[0]);
    }
    setNewMessage(msg);
    setShowTemplates(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getMessageTypeIcon = (type) => {
    switch (type) {
      case 'ai_drafted':
        return <Sparkles className="w-3 h-3 text-purple-500" />;
      case 'system_notification':
        return <Bell className="w-3 h-3 text-blue-500" />;
      default:
        return null;
    }
  };

  const getRiskBadge = (score) => {
    if (score <= 2) return <Badge className="bg-red-100 text-red-700 text-xs">Critical</Badge>;
    if (score === 3) return <Badge className="bg-yellow-100 text-yellow-700 text-xs">Monitor</Badge>;
    return <Badge className="bg-green-100 text-green-700 text-xs">Good</Badge>;
  };

  const filteredThreads = threads.filter(thread => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!thread.patient_name.toLowerCase().includes(query) &&
          !thread.patient_email.toLowerCase().includes(query)) {
        return false;
      }
    }
    if (filterRisk !== 'all') {
      const riskValue = parseInt(filterRisk);
      if (thread.risk_score !== riskValue) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-64"></div>
        <div className="grid grid-cols-3 gap-4 h-[600px]">
          <div className="skeleton col-span-1"></div>
          <div className="skeleton col-span-2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="messages-inbox">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Messages
          </h1>
          <p className="text-gray-600 mt-1">Communicate with your members</p>
        </div>
        <Button
          onClick={loadData}
          variant="outline"
          className="rounded-full"
          data-testid="refresh-messages-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-220px)] min-h-[500px]">
        {/* Thread List */}
        <Card className="lg:col-span-1 flex flex-col">
          <CardHeader className="pb-3">
            <div className="space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-threads"
                />
              </div>
              <Select value={filterRisk} onValueChange={setFilterRisk}>
                <SelectTrigger className="w-full" data-testid="risk-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by risk" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Members</SelectItem>
                  <SelectItem value="1">Critical (1)</SelectItem>
                  <SelectItem value="2">High (2)</SelectItem>
                  <SelectItem value="3">Monitor (3)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-auto p-0">
            <div className="divide-y divide-gray-100">
              {filteredThreads.map((thread) => (
                <div
                  key={thread.patient_id}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedThread?.patient_id === thread.patient_id ? 'bg-purple-50 border-l-4 border-purple-500' : ''
                  }`}
                  onClick={() => setSelectedThread(thread)}
                  data-testid={`thread-${thread.patient_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <p className="font-semibold text-sm truncate">{thread.patient_name}</p>
                          {getRiskBadge(thread.risk_score)}
                        </div>
                        <p className="text-xs text-gray-500 truncate">
                          {thread.last_message?.message || 'No messages yet'}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      <span className="text-xs text-gray-400">
                        {thread.last_message ? formatTime(thread.last_message.created_at) : ''}
                      </span>
                      {thread.unread_count > 0 && (
                        <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-purple-600 rounded-full">
                          {thread.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {filteredThreads.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No conversations found</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Chat Area */}
        <Card className="lg:col-span-2 flex flex-col">
          {selectedThread ? (
            <>
              {/* Chat Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{selectedThread.patient_name}</h3>
                    <p className="text-xs text-gray-500">{selectedThread.patient_email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getRiskBadge(selectedThread.risk_score)}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/patients/${selectedThread.patient_id}`)}
                    data-testid="view-patient-btn"
                  >
                    View Profile
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-auto p-4 space-y-4" data-testid="messages-container">
                {messages.map((message, index) => {
                  const isFromProvider = message.from_user_id !== selectedThread.patient_id;
                  return (
                    <div
                      key={message.id || index}
                      className={`flex ${isFromProvider ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${isFromProvider ? 'order-2' : ''}`}>
                        <div
                          className={`rounded-2xl px-4 py-3 ${
                            isFromProvider
                              ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <p className="text-sm">{message.message}</p>
                        </div>
                        <div className={`flex items-center space-x-2 mt-1 text-xs text-gray-400 ${isFromProvider ? 'justify-end' : ''}`}>
                          {getMessageTypeIcon(message.message_type)}
                          <span>{formatMessageTime(message.created_at)}</span>
                          {isFromProvider && (
                            message.read_by_recipient ? 
                              <CheckCheck className="w-3 h-3 text-blue-500" /> :
                              <Check className="w-3 h-3" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Composer */}
              <div className="p-4 border-t border-gray-200">
                {/* Templates Panel */}
                {showTemplates && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Quick Templates</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowTemplates(false)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {templates.map((template) => (
                        <Button
                          key={template.id}
                          variant="outline"
                          size="sm"
                          className="text-left justify-start h-auto py-2"
                          onClick={() => handleUseTemplate(template)}
                          data-testid={`template-${template.id}`}
                        >
                          <div>
                            <p className="font-medium text-xs">{template.name}</p>
                            <p className="text-xs text-gray-500 truncate">{template.body.substring(0, 40)}...</p>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-end space-x-2">
                  <div className="flex-1">
                    <Textarea
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="min-h-[80px] resize-none"
                      data-testid="message-input"
                    />
                    <div className="flex items-center space-x-2 mt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowTemplates(!showTemplates)}
                        data-testid="toggle-templates-btn"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Templates
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setNewMessage(newMessage + ' 💧')}
                      >
                        <Smile className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <Button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className="rounded-full px-6"
                    style={{ background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' }}
                    data-testid="send-message-btn"
                  >
                    {sendingMessage ? 'Sending...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="font-semibold text-lg mb-2">Select a conversation</h3>
                <p className="text-sm">Choose a member from the list to view messages</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default MessagesInbox;
